import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import ParticipantQuestionnaire from './ParticipantQuestionnaire';
import SpeedRunParticipant from './SpeedRunParticipant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const getWsUrl = () => {
  if (BACKEND_URL && BACKEND_URL.startsWith('http')) {
    return BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  }
  // If relative or empty, use window location
  const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${protocol}${window.location.host}`;
};
const WS_URL = getWsUrl();

const Participant = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [senderName, setSenderName] = useState('');
  const [nameSet, setNameSet] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('setup');
  const [summary, setSummary] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [participantId, setParticipantId] = useState('');
  const [sessionFull, setSessionFull] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const inputContainerRef = useRef(null);
  const hasInitialized = useRef(false);
  const hasJoined = useRef(false);
  const sessionStatusRef = useRef(sessionStatus);

  // Keep ref synced with sessionStatus
  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  useEffect(() => {
    // Prevent duplicate initialization (React StrictMode runs effects twice)
    if (hasInitialized.current) {
      return;
    }
    
    const initialize = async () => {
      hasInitialized.current = true;
      
      // First fetch session to check type
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}`);
        const sessionData = response.data;
        
        // If questionnaire or speed run quiz, let those components handle join logic
        if (sessionData.session_type === 'questionnaire' || sessionData.session_type === 'speed_run_quiz') {
          return;
        }
      } catch (error) {
        console.error('Failed to fetch session:', error);
      }
      
      // Only proceed with regular session join for non-special session types
      fetchSession();
      fetchMessages();

      // Generate or retrieve participant ID for deduplication (use localStorage for persistence across scans)
      let pid = localStorage.getItem(`participant_id_${sessionId}`);
      if (!pid) {
        pid = `participant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(`participant_id_${sessionId}`, pid);
        console.log('Generated new participant_id:', pid);
      } else {
        console.log('Reusing existing participant_id:', pid);
      }
      setParticipantId(pid);

      // Join session via REST API (fallback for WebSocket issues)
      await joinSession(pid);

      // Try WebSocket connection
      connectWebSocket(pid);
    };
    
    initialize();

    // Check for saved name
    const savedName = localStorage.getItem(`participant_name_${sessionId}`);
    if (savedName) {
      setSenderName(savedName);
      setNameSet(true);
    }

    // Reduced polling for better performance (WebSocket handles real-time updates)
    // Only poll for session status as fallback
    const pollInterval = setInterval(() => {
      fetchSession();
    }, 10000); // 10 seconds (was 2 seconds)

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      clearInterval(pollInterval);
    };
  }, [sessionId]);

  useEffect(() => {
    // Only auto-scroll if user is not actively typing
    // This prevents the input from being pushed behind the keyboard
    if (document.activeElement !== textareaRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`);
      const sessionData = response.data;
      
      // If questionnaire or speed run quiz, render appropriate component
      if (sessionData.session_type === 'questionnaire' || sessionData.session_type === 'speed_run_quiz') {
        // Don't set session state, let the specific component handle it
        return;
      }
      
      // Prevent reverting to 'setup' if we are already 'active' or 'completed'
      if ((sessionStatusRef.current === 'active' || sessionStatusRef.current === 'completed') && 
          sessionData.status === 'setup') {
        console.log('Ignoring stale session data (setup) while active/completed');
        return;
      }
      
      setSession(sessionData);
      setSessionStatus(sessionData.status);

      // Fetch summary if session is completed
      if (sessionData.status === 'completed' && !summary) {
        fetchSummary();
      }

      // Generate random name if anonymous
      if (sessionData.is_anonymous && !localStorage.getItem(`participant_name_${sessionId}`)) {
        const randomName = `User${Math.random().toString(36).substring(2, 8)}`;
        setSenderName(randomName);
        localStorage.setItem(`participant_name_${sessionId}`, randomName);
        setNameSet(true);
      }
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const fetchSummary = async () => {
    try {
      // Note: This endpoint might need authentication, but we'll try without for participants
      const response = await axios.get(`${API}/sessions/${sessionId}/summary`);
      setSummary(response.data);
    } catch (error) {
      console.log('Summary not available yet');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages', error);
    }
  };

  const joinSession = async (participantId) => {
    // Prevent duplicate join calls
    if (hasJoined.current) {
      console.log('Already joined, skipping duplicate join call');
      return;
    }
    hasJoined.current = true;
    
    try {
      const response = await axios.post(`${API}/sessions/${sessionId}/join?participant_id=${participantId}`);
      console.log('✓ Joined session, participant count:', response.data.participant_count);
      setSessionFull(false);
    } catch (error) {
      console.error('Failed to join session', error);
      hasJoined.current = false; // Reset on error so user can retry
      // Check if session is full (403 error)
      if (error.response?.status === 403) {
        setSessionFull(true);
        toast.error(error.response?.data?.detail || "Session is full");
      }
    }
  };

  const toggleVote = async (messageId) => {
    try {
      const response = await axios.post(`${API}/messages/${messageId}/vote?participant_id=${participantId}`);
      
      // Update local messages state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, hearts: response.data.hearts, hearted_by: response.data.voted ? [...(msg.hearted_by || []), participantId] : (msg.hearted_by || []).filter(id => id !== participantId) }
          : msg
      ));
      
      toast.success(response.data.voted ? 'Voted!' : 'Vote removed');
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const connectWebSocket = (participantId) => {
    // Prevent duplicate connections - check all connection states
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        console.log('WebSocket already connecting/connected, skipping duplicate. State:', state);
        return;
      }
    }
    
    console.log('DEBUG - BACKEND_URL:', BACKEND_URL);
    console.log('DEBUG - WS_URL:', WS_URL);
        const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
        const wsUrl = `${protocol}${window.location.host}/api/ws/${sessionId}?participant_id=${participantId}`;
    console.log('Participant connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    let pingInterval;

    ws.onopen = () => {
      console.log('✓ Participant WebSocket connected successfully');
      // Send ping every 30 seconds to keep connection alive
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Participant received:', data);

      if (data.type === 'new_message') {
        setMessages(prev => {
          // Avoid duplicates - check if message already exists
          const exists = prev.some(m => m.id === data.message.id);
          if (exists) {
            console.log('Duplicate message detected, skipping:', data.message.id);
            return prev;
          }
          console.log('Adding new message:', data.message.id);
          return [...prev, data.message];
        });
      } else if (data.type === 'session_started') {
        setSessionStatus('active');
        toast.success('Session has started!');
      } else if (data.type === 'session_ended') {
        setSessionStatus('completed');
        toast.info('Session has ended. Thank you for participating!');
        // Fetch summary to show voting interface
        fetchSummary();
      }
    };

    ws.onerror = (error) => {
      console.error('Participant WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('Participant WebSocket disconnected');
      if (pingInterval) {
        clearInterval(pingInterval);
      }
    };

    wsRef.current = ws;
  };

  const scrollToBottom = () => {
    // Scroll to show latest message but keep input visible
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (senderName.trim()) {
      localStorage.setItem(`participant_name_${sessionId}`, senderName);
      setNameSet(true);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();

    if (!content.trim()) {
      return;
    }

    if (sessionStatus !== 'active') {
      return;
    }

    try {
      await axios.post(`${API}/sessions/${sessionId}/messages`, {
        content,
        sender_name: senderName
      });

      // Message will appear via WebSocket broadcast (no need for optimistic update)
      // WebSocket is fast enough to provide real-time feedback
      setContent('');
      
      // Keep input visible and focused - DON'T scroll
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Input is already at bottom, just maintain focus
        }
      }, 50);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Check if we need to fetch session type first
  const [sessionType, setSessionType] = useState(null);
  const [checkingType, setCheckingType] = useState(true);
  
  useEffect(() => {
    const checkSessionType = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}`);
        setSessionType(response.data.session_type);
        setCheckingType(false);
      } catch (error) {
        setCheckingType(false);
      }
    };
    checkSessionType();
  }, []);
  
  if (checkingType) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }
  
  // Route to appropriate component based on session type
  if (sessionType === 'questionnaire') {
    return <ParticipantQuestionnaire />;
  }
  
  if (sessionType === 'speed_run_quiz') {
    return <SpeedRunParticipant />;
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  // Show "session full" screen
  if (sessionFull) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="mb-6">
                <div className="w-24 h-24 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-5xl">🚫</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-3">Too Crowded!</h1>
                <p className="text-xl text-gray-600 mb-2">Can't get in..</p>
                <p className="text-sm text-gray-500">
                  This session has reached its maximum capacity of {session.max_participants} participants.
                </p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <p className="text-orange-800 session-text">
                  <strong>Session:</strong> {session.topic}
                </p>
              </div>
              <p className="text-xs text-gray-400">
                Please try again later or contact the facilitator
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!nameSet && !session.is_anonymous) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-2xl inline-block mb-4">
                <MessageCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome!</h2>
              <p className="text-gray-600">Please enter your name to join the session</p>
            </div>

            <form onSubmit={handleNameSubmit} className="space-y-4">
              <Input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your name"
                className="text-lg p-6 rounded-xl"
                data-testid="name-input"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-6 rounded-xl"
                data-testid="join-button"
              >
                Join Session
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If session is completed, show summary and voting interface
  if (sessionStatus === 'completed' && summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
        {/* Header */}
        <div className="bg-white border-b shadow-sm sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4 max-w-4xl">
            <div className="flex items-center justify-center mb-2">
              <PollarBrandMini />
            </div>
            <h1 className="font-bold text-center text-indigo-900 session-title px-2">
              {session.topic}
            </h1>
            <p className="text-center text-gray-600 mt-1 session-text">Session Results - Vote for your favorite ideas!</p>
          </div>
        </div>

        {/* Clusters List */}
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          {!selectedCluster ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold mb-4">Idea Clusters</h2>
              {[...summary.clusters]
                .sort((a, b) => b.message_count - a.message_count)
                .map((cluster, index) => (
                  <div
                    key={cluster.id}
                    onClick={() => setSelectedCluster(cluster)}
                    className="bg-white rounded-xl p-6 shadow-lg cursor-pointer hover:shadow-xl transition-all border-2 hover:border-indigo-500"
                    data-testid={`cluster-${cluster.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-indigo-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                            #{index + 1}
                          </span>
                          <h3 className="text-xl font-bold text-gray-900">{cluster.title}</h3>
                        </div>
                        <p className="text-gray-600">{cluster.message_count} ideas</p>
                      </div>
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div>
              {/* Back to clusters */}
              <button
                onClick={() => setSelectedCluster(null)}
                className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-4 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Clusters
              </button>

              <h2 className="text-2xl font-bold mb-6">{selectedCluster.title}</h2>

              {/* Messages with voting */}
              <div className="space-y-4">
                {messages
                  .filter(msg => selectedCluster.message_ids.includes(msg.id))
                  .map(msg => {
                    const hasVoted = msg.hearted_by?.includes(participantId);
                    return (
                      <div key={msg.id} className="bg-white rounded-xl p-6 shadow-lg">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-indigo-600">{msg.sender_name}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-gray-800 leading-relaxed">{msg.content}</p>
                          </div>

                          {/* Vote Button */}
                          <button
                            onClick={() => toggleVote(msg.id)}
                            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
                              hasVoted
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            data-testid={`vote-${msg.id}`}
                          >
                            <svg
                              className={`w-6 h-6 ${hasVoted ? 'fill-current' : 'stroke-current fill-none'}`}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                              />
                            </svg>
                            <span className="text-sm font-bold">{msg.hearts || 0}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Simplified Header - Mobile Optimized - Always Visible */}
      <div className="bg-white shadow-md flex-shrink-0 sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex-shrink-0">
              <PollarBrandMini />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                {senderName.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                {senderName}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area - Scrollable */}
      <div className="flex-1 overflow-y-auto overscroll-behavior-contain">
        <div className="px-4 py-4 pb-8">
          <div className="space-y-3 max-w-2xl mx-auto">
            {messages.filter(msg => msg.sender_name === senderName).length === 0 ? (
              <div className="text-center py-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-4">
                  <MessageCircle className="w-12 h-12 text-white/80 mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">No ideas yet!</p>
                  <p className="text-white/70 text-sm">Type your first idea below 👇</p>
                </div>
              </div>
            ) : (
              <>
                {messages
                  .filter(msg => msg.sender_name === senderName)
                  .map((msg, index) => (
                    <div
                      key={msg.id}
                      className="bg-white rounded-2xl p-4 shadow-lg animate-slideIn"
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`message-${msg.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {msg.sender_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-semibold text-indigo-600 text-sm truncate">{msg.sender_name}</span>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-gray-800 leading-relaxed pl-10">{msg.content}</p>
                    </div>
                  ))}
              </>
            )}
            <div ref={messagesEndRef} className="h-4" />
          </div>
        </div>
      </div>

      {/* Input Area - Fixed at Bottom (Mobile Safe with Extra Padding) */}
      {sessionStatus === 'active' && (
        <div 
          ref={inputContainerRef}
          className="bg-white shadow-lg flex-shrink-0" 
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 60px)' }}
        >
          <div className="px-4 py-4 max-w-2xl mx-auto">
            <div className="flex gap-2 items-end">
              {/* Text Input */}
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue.length <= 50) {
                      setContent(newValue);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (content.trim() && content.length <= 50) {
                        handleSendMessage();
                      }
                    }
                  }}
                  placeholder="Type your idea here..."
                  className="w-full resize-none rounded-2xl text-base p-3 pr-12 border-2 border-gray-200 focus:border-purple-500 transition-colors"
                  rows="2"
                  data-testid="message-input"
                  maxLength={50}
                />
                {/* Character Counter inside input */}
                <div className={`absolute bottom-2 right-2 text-xs font-bold px-2 py-1 rounded-full ${
                  content.length > 45 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {content.length}/50
                </div>
              </div>

              {/* Send Button */}
              <Button
                onClick={handleSendMessage}
                disabled={!content.trim() || content.length > 50}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl h-[72px] w-16 flex-shrink-0 p-0 shadow-lg"
                data-testid="send-button"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participant;